import {
  Box,
  Typography,
  TextField,
  Paper,
  Button,
  CircularProgress,
  Slide,
  AppBar,
  Toolbar,
} from "@mui/material";
import { useState, useEffect } from "react";
import { getAboutUs } from "../../services/api/getAll";
import { addAboutUs, deleteAboutUs } from "../../services/api/adminService";

const languages = ["en", "ar", "es"];

const defaultForm = {
  en: { content: "" },
  ar: { content: "" },
  es: { content: "" },
};

const AboutUsForm = () => {
  const [form, setForm]: any = useState(defaultForm);
  const [initialForm, setInitialForm] = useState(defaultForm);
  const [isChanged, setIsChanged] = useState(false);
  const [loading, setLoading] = useState(true);
  const isMobile = window.innerWidth < 600;
  const [id, setId] = useState("");
  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await getAboutUs();
        setId(response?.items?.[response?.items?.length - 1]?.id);
        const translations = response?.items?.[response?.items?.length - 1]?.translations || [];
        const loaded: any = { ...defaultForm };
        translations.forEach(({ languageCode, content }: any) => {
          loaded[languageCode] = { content };
        });
        setForm(loaded);
        setInitialForm(loaded);
      } catch (e) {
        console.error("Error loading about us", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    setIsChanged(JSON.stringify(form) !== JSON.stringify(initialForm));
  }, [form, initialForm]);

  const handleChange = (lang: string, value: string) => {
    setForm((prev: any) => ({
      ...prev,
      [lang]: { content: value },
    }));
  };

  const handleSave = async () => {
    const translations = languages.map((lang) => ({
      languageCode: lang,
      content: form[lang].content,
    }));

    try {
      await addAboutUs({ translations });
      setInitialForm(form);
      setIsChanged(false);
    } catch (error) {
      console.error("Failed to save About Us content:", error);
    }
  };

  if (loading) {
    return (
      <Box
        height="80vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 2, md: 5 },
        bgcolor: "#f4f7fa",
        fontFamily: "Poppins",
        background: "linear-gradient(135deg, #f9f9fb, #e6eef6)",
      }}
    >
      {/* Floating Save Bar */}
      <Slide direction="down" in={isChanged}>
        <AppBar
           
          elevation={3}
          color="default"
          sx={{
            top:isMobile ? 60 : undefined,
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(12px)",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Toolbar
            sx={{
              justifyContent: "space-between",
              px: 4,
              py: 1.5,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ color: "#333" }}
            >
              Unsaved Changes
            </Typography>
            <Button
              onClick={handleSave}
              variant="contained"
              size="medium"
              sx={{
                background: "#007aff",
                borderRadius: 2,
                px: 4,
                fontWeight: "bold",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#005bb5",
                },
              }}
            >
              Save
            </Button>
          </Toolbar>
        </AppBar>
      </Slide>

      {/* Header */}
      <Box sx={{ mt: isChanged ? 10 : 2, mb: 5 }}>
        <Typography variant="h3" fontWeight="bold" color="#1a1a1a">
          About Us
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage your content across multiple languages
        </Typography>
      </Box>

      {/* Language Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 4,
        }}
      >
        {languages.map((lang) => (
          <Paper
            key={lang}
            elevation={8}
            sx={{
              p: 4,
              borderRadius: "20px",
              backdropFilter: "blur(12px)",
              background: "#ffffffee",
              border: "1px solid #e0e0e0",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.06)",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="h6"
              fontWeight="600"
              color="primary"
              sx={{ letterSpacing: 1, textTransform: "uppercase" }}
            >
              {lang}
            </Typography>

            <TextField
              label={`Write About Us in ${lang.toUpperCase()}`}
              multiline
              fullWidth
              minRows={7}
              value={form[lang].content}
              onChange={(e) => handleChange(lang, e.target.value)}
              sx={{
                fontFamily: "Poppins",
                background: "#fafafa",
                borderRadius: 2,
                "& .MuiOutlinedInput-root": {
                  color: "#000", // Text inside input
                  "& textarea": {
                    color: "#000",
                    fontFamily: "Poppins",
                  },
                  "& fieldset": {
                    borderColor: "#ccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#007aff",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#007aff",
                  },
                },
              }}
              InputLabelProps={{
                sx: {
                  color: "#444",
                  fontFamily: "Poppins",
                },
              }}
            />
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default AboutUsForm;
